import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import styled from 'styled-components'

import { useReferralsInfo } from 'api/referrals/useReferralsInfo'
import * as spriteIcons from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
// import ChangeStateTabs from 'components/UI/ChangeStateTabs/ChangeStateTabs'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { getLastPeriodFn } from 'features/AnalyticsPeriodDropdown/AnalyticsPeriodDropdown'
import BarChart from 'features/BarChart/BarChart'
import { getLink } from 'helpers/appLinks'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { Summary } from 'pages/referrals/view/Summary'
import { getSalesAnalytics } from 'services/endpoints/analytic'
import { type BarPoint, useReferralsGraphCacheStore } from 'store/referralsGraphCacheStore'
import { useReferralsInfoCacheStore } from 'store/referralsInfoCacheStore'
import { CHART_TYPES } from 'types/api/analytic'

const chartOptionToTranslateKey = {
  last7Days: 'Last 7 Days',
  last30Days: 'Last 30 Days',
  last12Months: 'Last 12 Months',
} as const

export enum TABS {
  CREATORS = 'Creators',
  FANS = 'Fans',
}

type ChartOpt = keyof typeof chartOptionToTranslateKey

type EarningsGraphRow = {
  day?: string
  month?: string
  sum: number
}

const parseChartDate = (value: string) => {
  if (/^\d{4}-\d{2}$/.test(value)) return new Date(`${value}-01T00:00:00Z`)
  return new Date(value)
}

const fmtTooltipDate = (value: string, opt: ChartOpt) => {
  const date = parseChartDate(value)

  if (opt === 'last12Months') {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    })
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const fmtX = (value: string, opt: ChartOpt) => {
  const date = parseChartDate(value)

  if (opt === 'last7Days') {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      timeZone: 'UTC',
    })
  }

  if (opt === 'last30Days') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  })
}

const getPeriodForOpt = (opt: ChartOpt) => {
  if (opt === 'last12Months') {
    const period = getLastPeriodFn(11, 'month')()
    return { start: period.start, end: period.end }
  }

  if (opt === 'last30Days') {
    const period = getLastPeriodFn(1, 'month')()
    return { start: period.start, end: period.end }
  }

  const period = getLastPeriodFn(6, 'day')()
  return { start: period.start, end: period.end }
}

export const ReferralsTab = () => {
  const { t } = useTranslation()

  const [chartOptionsOpen, setChartOptionsOpen] = useState(false)
  const [selectedChartOption, setSelectedChartOption] = useState<ChartOpt>('last7Days')
  const [activeTab, setActiveTab] = useState<TABS>(TABS.CREATORS)
  const [chartOpen, setChartOpen] = useState(false)

  const referralLinkRef = useRef<HTMLDivElement | null>(null)

  /*
   * Referral info
   */
  const cachedInfoMessage = useReferralsInfoCacheStore(state => state.message)
  const setMessage = useReferralsInfoCacheStore(state => state.setMessage)

  const { data: referralsInfoData, isFetching: fetchingReferralInfo } = useReferralsInfo()
  const info = referralsInfoData?.message

  useEffect(() => {
    if (!info) return

    setMessage({
      balance: info.balance,
      code: info.code,
      referred_fan_count: info.referred_fan_count,
      referred_creator_count: info.referred_creator_count,
    })
  }, [
    info?.balance,
    info?.code,
    info?.referred_fan_count,
    info?.referred_creator_count,
    setMessage,
  ])

  const balanceValue = cachedInfoMessage?.balance != null
    ? Number(cachedInfoMessage.balance)
    : fetchingReferralInfo
      ? null
      : Number(info?.balance ?? 0)

  const codeText = cachedInfoMessage?.code ?? info?.code ?? ''

  const creatorsCount = Number(
    cachedInfoMessage?.referred_creator_count ?? info?.referred_creator_count ?? 0
  )

  const fansCount = Number(
    cachedInfoMessage?.referred_fan_count ?? info?.referred_fan_count ?? 0
  )

  /*
   * Referral earnings graph
   */
  const getCachedPoints = useReferralsGraphCacheStore(state => state.getPoints)
  const setGraph = useReferralsGraphCacheStore(state => state.setGraph)

  const cachedGraphPoints = useMemo(
    () => getCachedPoints(selectedChartOption),
    [getCachedPoints, selectedChartOption]
  )

  const { data: graphData = [] } = useQuery<BarPoint[]>(
    ['referralsGraph', selectedChartOption],
    async () => {
      const period = getPeriodForOpt(selectedChartOption)

      const res = await getSalesAnalytics({
        dateFrom: period.start,
        dateTo: period.end,
        chart: CHART_TYPES.referral_revenue,
      })

      const rows: EarningsGraphRow[] = res?.chart ?? []

      return rows.flatMap(row => {
        const date = row.day ?? row.month
        if (!date) return []

        return [{
          x: fmtX(date, selectedChartOption),
          y: Number(row.sum) || 0,
          date: fmtTooltipDate(date, selectedChartOption),
        }]
      })
    },
    {
      enabled: chartOpen,
      initialData: () => cachedGraphPoints.length ? cachedGraphPoints : undefined,
      keepPreviousData: true,
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
      onSuccess: points => {
        const total = points.reduce((sum, point) => sum + (Number(point.y) || 0), 0)
        setGraph(selectedChartOption, points, Number(total.toFixed(2)))
      },
    }
  )

  const displayedTotal = graphData.reduce((sum, point) => sum + (Number(point.y) || 0), 0)

  /*
   * Referral link
   */
  const handleCopy = () => {
    if (!codeText) return
    void navigator.clipboard.writeText(`${getLink.referrLink()}${codeText}`)
  }

  const handleReferralMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (window.matchMedia('(pointer: coarse)').matches || event.detail !== 2) return

    event.preventDefault()

    const element = referralLinkRef.current
    const selection = window.getSelection()

    if (!element || !selection) return

    const range = document.createRange()
    range.selectNodeContents(element)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const changeSelectedChartOption = (chartOpt: ChartOpt) => {
    setSelectedChartOption(chartOpt)
    setChartOptionsOpen(false)
  }

  return (
    <SummarySection>
      <div className='balance-container'>
        {!chartOpen ? (
          <>
            <div className='balance-text'>{t('Total Earned')}</div>

            <div className='balance-number-referrals'>
              <spriteIcons.IconDollar width='15' color='white' />

              <div className='total-earned-text'>
                {balanceValue === null ? '...' : balanceValue.toFixed(2)}
              </div>

              <span className='balance-number-currency-text'>(USD)</span>
            </div>

            <div className='balance-options'>
              <div
                ref={referralLinkRef}
                className='referral-link'
                onMouseDown={handleReferralMouseDown}
                onTouchEnd={event => {
                  event.preventDefault()
                  void handleCopy()
                }}
                onContextMenu={event => {
                  if (window.matchMedia('(pointer: coarse)').matches) event.preventDefault()
                }}
              >
                <span className='referral-link__url'>{getLink.referrLink()}</span>
                <span className='referral-link__code'>{codeText}</span>
              </div>

              <IconButton
                icon={<spriteIcons.IconGraph />}
                size='big'
                type='black'
                clickFn={() => setChartOpen(true)}
              />
            </div>
          </>
        ) : (
          <div className='balance-chart'>
            <div className='balance-chart-options'>
              <div className='balance-chart-currency'>
                <div className='balance-number'>
                  <spriteIcons.IconDollar width='30' height='30' color='white' />

                  <div className='balance-amount'>
                    <div className='balance-number-graph-open'>
                      {displayedTotal.toFixed(2)}
                    </div>
                    <span className='balance-usd'>(USD)</span>
                  </div>
                </div>

                <div className='balance-overall'>{t('referralRevenue')}</div>
              </div>

              <div
                className='balance-chart-select'
                onClick={() => setChartOptionsOpen(open => !open)}
              >
                <div className='balance-chart-selected'>
                  {t('by')} <span>{chartOptionToTranslateKey[selectedChartOption]}</span>
                </div>

                <div
                  className={`balance-chart-select-list ${
                    chartOptionsOpen ? 'balance-chart-select-list-open' : ''
                  }`}
                >
                  <div
                    className='balance-chart-select-option'
                    onClick={() => changeSelectedChartOption('last7Days')}
                  >
                    {t('by')} <span>{t('last7Days')}</span>
                  </div>

                  <div
                    className='balance-chart-select-option'
                    onClick={() => changeSelectedChartOption('last30Days')}
                  >
                    {t('by')} <span>{t('last30Days')}</span>
                  </div>

                  <div
                    className='balance-chart-select-option'
                    onClick={() => changeSelectedChartOption('last12Months')}
                  >
                    {t('by')} <span>{t('last12Months')}</span>
                  </div>
                </div>
              </div>

              <div className='balance-chart-close' onClick={() => setChartOpen(false)}>
                <spriteIcons.IconClose />
              </div>
            </div>

            <BarChart
              chartData={graphData}
              datasetOptions={{
                borderRadius: 15,
                barThickness: 9,
              }}
            />
          </div>
        )}
      </div>

      <WithHeaderSection
        customClass='referrals'
        
/*         headerSection={
          <ChangeStateTabs
            activeTab={activeTab}
            tabs={[
              {
                name: (
                  <TabLabel>
                    {t(TABS.CREATORS)}
                    {creatorsCount > 0 && <TabCount>{creatorsCount}</TabCount>}
                  </TabLabel>
                ),
                value: TABS.CREATORS,
              },
              {
                name: (
                  <TabLabel>
                    {t(TABS.FANS)}
                    {fansCount > 0 && <TabCount>{fansCount}</TabCount>}
                  </TabLabel>
                ),
                value: TABS.FANS,
              },
            ]}
            clickFn={value => setActiveTab(value as TABS)}
            width='fit'
          />
        } */
      >
        <Summary tab={activeTab} />
      </WithHeaderSection>
    </SummarySection>
  )
}

const SummarySection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
`

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
`

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  flex: 0 0 1.6rem;
  border-radius: 50%;
  background: linear-gradient(92.95deg, #53aff7 -12.15%, #2894ff 111.4%);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
  transform: translateY(-2px);
`